/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { HomeErrorBoundary } from '../../../components/HomeErrorBoundary';
import { HomePage } from '@airasia-rn/home/pages';
import { HomeNavigationContext, useHomeTileRedirect } from '@airasia-rn/home';
import { useAskBoChatBot } from '@airasia-rn/web/hooks';
import { useAppConfig } from '@airasia-rn/ui/config';
import { useDeviceInfo } from '@airasia-rn/ui';
import { usePrefetchProductRoutes } from '../../../hooks/usePrefetchProductRoutes';
import { useViewTransitionRouter } from '@airasia-rn/web/contexts/ViewTransitionsProvider';
import { lobPath, localeToPath } from '@airasia-rn/web/utils/localePath';
import { isProductTileSlug } from '../../../constants/productSlugs';

const SEARCH_SLUG_TO_PRODUCT_ID: Record<string, string> = {
  flight: 'flights',
  hotels: 'hotels',
  snap: 'snap',
};

// Both singular and plural product ids resolve to the routable URL slug, which
// is 'flight' (singular) but 'hotels' (plural) — see productSlugs.json.
const PRODUCT_TILE_SLUG_BY_ID: Record<string, string> = {
  flight: 'flight',
  flights: 'flight',
  hotel: 'hotels',
  hotels: 'hotels',
  snap: 'snap',
};

const getProductTileSlug = (productId: string) =>
  PRODUCT_TILE_SLUG_BY_ID[productId.trim().toLowerCase()];

// URL segments are anchored on the locale/country pair rather than a fixed
// position, so any path prefix ahead of them is preserved. The
// locale identifier (e.g. 'en-gb') maps to two URL segments (e.g. 'en'/'gb').
// Public URLs are LOB-first (/snap/en/gb), so the product-tile slug — when
// present — is the segment immediately BEFORE the locale.
const getLocalePathSegments = (locale: string) => {
  const [localeSeg, countrySeg] = localeToPath(locale).split('/');
  const segments = window.location.pathname.split('/').filter(Boolean);
  for (let i = 0; i < segments.length - 1; i++) {
    if (
      segments[i]?.toLowerCase() === localeSeg &&
      segments[i + 1]?.toLowerCase() === countrySeg
    ) {
      const hasLob = i > 0 && isProductTileSlug(segments[i - 1]);
      return {
        segments,
        localeIndex: i,
        countryIndex: i + 1,
        // Index of the LOB slug, or null on home.
        lobIndex: hasLob ? i - 1 : null,
      };
    }
  }
  return null;
};

let didNavigateInApp = false;

export default function HomePageClient({
  searchWidgetSlug,
}: {
  searchWidgetSlug?: string;
}) {
  const vtRouter = useViewTransitionRouter();
  const router = useRouter();
  const { isMobileWidth } = useDeviceInfo();
  const wasMobileWidthRef = useRef<boolean | null>(null);
  const { resolveRedirectUrl } = useHomeTileRedirect();
  const { config } = useAppConfig();
  const {
    openChatWithBo,
    isReady: isAskBoReady,
    preload: preloadAskBo,
  } = useAskBoChatBot(config.locale, { hideDefaultLauncher: true });
  const initialSearchWidgetProductId = searchWidgetSlug
    ? SEARCH_SLUG_TO_PRODUCT_ID[searchWidgetSlug]
    : undefined;

  usePrefetchProductRoutes(config.locale, searchWidgetSlug);

  // Desktop tab selection keeps the URL truthful without re-rendering the
  // page: at desktop width the chrome of /{locale} and /{slug}/{locale} is
  // identical, so a shallow history update is enough.
  const handleSearchWidgetTabSelect = useCallback(
    (productId: string) => {
      const productTileSlug = getProductTileSlug(productId);
      const localePath = getLocalePathSegments(config.locale);
      if (!productTileSlug || !localePath) {
        return;
      }
      const { segments, localeIndex, countryIndex, lobIndex } = localePath;
      // The slug now sits BEFORE the locale, so the prefix must stop short of
      // any slug already in the URL — slicing to localeIndex would keep it and
      // stack them (snap -> flight giving /snap/flight/en/gb).
      const prefixSegments = segments.slice(0, lobIndex ?? localeIndex);
      // Raw history URL: keep any path prefix and the trailing slash.
      const nextSegments = [
        ...prefixSegments,
        productTileSlug,
        segments[localeIndex],
        segments[countryIndex],
      ];
      window.history.replaceState(
        null,
        '',
        `/${nextSegments.join('/')}/${window.location.search}`,
      );
    },
    [config.locale],
  );

  // The mobile header/footer chrome is fixed per route segment on the server,
  // so crossing into mobile width must re-run the page whenever the shallow
  // URL points at a segment that was never rendered. Plain router.replace (not
  // vtRouter) because a resize is not a directional navigation. Mobile ->
  // desktop crossing needs nothing: the chrome differences are mobile-only.
  useEffect(() => {
    const wasMobileWidth = wasMobileWidthRef.current;
    wasMobileWidthRef.current = isMobileWidth;

    if (wasMobileWidth === false && isMobileWidth) {
      const localePath = getLocalePathSegments(config.locale);
      if (!localePath) {
        return;
      }
      const { segments, localeIndex, lobIndex } = localePath;
      const urlSlug =
        lobIndex === null ? undefined : segments[lobIndex]?.toLowerCase();
      if (urlSlug !== searchWidgetSlug) {
        // Pass the app-relative path (LOB slug, else locale segment, onward)
        // rather than location.pathname.
        const appPath = segments.slice(lobIndex ?? localeIndex).join('/');
        router.replace(`/${appPath}/${window.location.search}`);
      }
    }
  }, [config.locale, isMobileWidth, router, searchWidgetSlug]);

  const handleBackPress = useCallback(() => {
    if (didNavigateInApp) {
      vtRouter.back('back');
    } else {
      vtRouter.replace(lobPath(config.locale), 'back');
    }
  }, [config.locale, vtRouter]);

  const handleProductTilePress = useCallback(
    (redirectionUrl: string, productId: string) => {
      const productTileSlug = getProductTileSlug(productId);

      if (productTileSlug) {
        didNavigateInApp = true;
        vtRouter.push(lobPath(config.locale, productTileSlug), 'forward');
        return;
      }

      const finalUrl = resolveRedirectUrl(productId, redirectionUrl);
      if (finalUrl) {
        window.open(finalUrl, '_blank', 'noopener,noreferrer');
      }
    },
    [config.locale, resolveRedirectUrl, vtRouter],
  );

  const homeNavigation = useMemo(
    () => ({
      navigateToProductTile: handleProductTilePress,
      navigateToNotificationBanner: (actionUrl?: string) => {
        if (actionUrl) {
          window.open(actionUrl, '_blank', 'noopener,noreferrer');
        }
      },
    }),
    [handleProductTilePress],
  );

  return (
    <HomeErrorBoundary
      locale={config.locale}
      currency={config.currency}
      geoId={config.geoId}
    >
      <HomeNavigationContext.Provider value={homeNavigation}>
        <HomePage
          initialSearchWidgetProductId={initialSearchWidgetProductId}
          onSearchWidgetTabSelect={handleSearchWidgetTabSelect}
          onBackPress={handleBackPress}
          onAskBotPress={isAskBoReady ? openChatWithBo : undefined}
          onAskBotHover={preloadAskBo}
          onAskBotTouchStart={preloadAskBo}
        />
      </HomeNavigationContext.Provider>
    </HomeErrorBoundary>
  );
}
